import { Box } from '@mui/material';
import * as d3 from 'd3';
import { useEffect, useRef, useState } from 'react';

interface IChartProps {
    data: { name: string, values: { month: string, value: number }[], isSmooth?: boolean }[],
    height: number,
    yAxisLabelType?: 'inPercent' | 'inValue' | 'custom';
    customLabelFormatter?: (value: number) => string;
    currency?: string;
    colors?: string[];
    customLegend: React.ReactNode
}

const formatNumber = (num: number, symbol: string, labelType: string) => {
    if (labelType === 'inPercent') {
        return `${d3.format(".0%")(num / 100)}`;
    } else if (labelType === 'inValue') {
        return `${symbol || ''} ${d3.format(".2s")(num).replace('k', 'K')}`;
    }
    return num.toString();
};

const AreaLineChart = ({ data, height = 400, customLabelFormatter, yAxisLabelType = 'inValue', currency, colors, customLegend }: IChartProps) => {
    const svgRef = useRef<SVGSVGElement | null>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState<number>(0);
    const [tooltipData, setTooltipData] = useState<any>(null);
    useEffect(() => {
        const wrapper = wrapperRef.current;

        const resizeObserver = new ResizeObserver((entries) => {
            if (entries[0]) {
                const newWidth = entries[0].contentRect.width;
                setContainerWidth(newWidth);
            }
        });

        if (wrapper) {
            resizeObserver.observe(wrapper);
        }

        return () => {
            if (wrapper) {
                resizeObserver.unobserve(wrapper);
            }
        };
    }, []);

    useEffect(() => {
        if (containerWidth === 0) return; // Wait for the container width

        const margin = { top: 20, right: 30, bottom: 30, left: 40 };
        const width = containerWidth - margin.left - margin.right;
        const chartHeight = height - margin.top - margin.bottom;

        // Clear the SVG
        d3.select(svgRef.current).selectAll('*').remove();

        const svg = d3
            .select(svgRef.current)
            .attr('width', containerWidth)
            .attr('height', height)
            .append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        // Define scales
        const x = d3
            .scalePoint()
            .domain(data[0]?.values?.map(d => d.month)) // Assuming all datasets share the same x-domain
            .range([0, width]);

        const y = d3
            .scaleLinear()
            .domain([0, d3.max(data, dataset => d3.max(dataset.values, d => d.value))!]) // Max value across all datasets
            .range([chartHeight, 0]);

        // Limit x-axis ticks dynamically based on width
        const numTicks = Math.floor(width / 80); // Adjust the factor (80) based on chart width
        const xAxis = d3.axisBottom(x).ticks(numTicks);

        const xAxisGroup = svg.append('g')
            .attr('transform', `translate(0,${chartHeight})`)
            .call(xAxis);

        xAxisGroup.selectAll("text")
            .attr("transform", "translate(0, 10)")
            .attr('color', '#8E8E93')
            .style("text-anchor", "middle");

        // Y Axis
        const yAxis = svg.append('g')
        yAxis.append('g').attr('color', '#8E8E93').attr("transform", `translate(-10,0)`)
            .call(d3.axisLeft(y).tickSize(-width).tickFormat((d: any) =>
                customLabelFormatter
                    ? customLabelFormatter(d)
                    : formatNumber(d, currency, yAxisLabelType)
            ).tickSizeOuter(0) as any)
            .selectAll('line')
            .attr('stroke-dasharray', '3,3')
            .attr('stroke', '#D3D3D4');

        xAxisGroup.selectAll(".tick line").remove();
        yAxis.select(".domain").attr("stroke", "none");
        xAxisGroup.select(".domain").attr("stroke", "none");

        // Define color scale for lines
        // const color = d3.scaleOrdinal(d3.schemeCategory10);

        // Line and area generators
        const line = (isSmooth: boolean) => d3.line<any>()
            .x(d => x(d.month)!)
            .y(d => y(d.value))
            .curve(isSmooth ? d3.curveMonotoneX : d3.curveLinear);  // Conditionally smooth the line

        const area = d3.area<any>()
            .x(d => x(d.month)!)
            .y0(chartHeight)
            .y1(d => y(d.value));

        // Iterate over each dataset to create lines and areas
        data.forEach((dataset, i) => {
            // const datasetColor = color(i.toString());

            // Create gradient for each area
            const gradient = svg.append('defs')
                .append('linearGradient')
                .attr('id', `area-gradient-${i}`)
                .attr('x1', '0%')
                .attr('x2', '0%')
                .attr('y1', '0%')
                .attr('y2', '100%');

            gradient.append('stop')
                .attr('offset', '0%')
                .attr('stop-color', colors[i])
                .attr('stop-opacity', 0.4);

            gradient.append('stop')
                .attr('offset', '100%')
                .attr('stop-color', colors[i])
                .attr('stop-opacity', 0.1);

            // Append the area with gradient
            svg.append('path')
                .datum(dataset.values)
                .attr('fill', `url(#area-gradient-${i})`)
                .attr('d', area);

            // Append the line with smoothness condition
            svg.append('path')
                .datum(dataset.values)
                .attr('fill', 'none')
                .attr('stroke', colors[i])
                .attr('stroke-width', 2)
                .attr('d', line(dataset.isSmooth || false));  // Use smoothness from the dataset

            // Append circles for hover effect
            {
                dataset?.showCircle &&
                    svg.selectAll(`circle-${i}`)
                        .data(dataset.values)
                        .enter()
                        .append('circle')
                        .attr('cx', d => x(d.month)!)
                        .attr('cy', d => y(d.value))
                        .attr('r', 5)
                        .attr('fill', colors[i])
                        .attr('stroke', '#fff')
                        .style('cursor', 'pointer')
                        .on('mouseover', function (event, d) {
                            setTooltipData({
                                x: `${event.pageX + 10}px`,
                                y: `${event.pageY - 30}px`,
                                content: `${dataset.name}: ${d.value}`
                            });
                            d3.select(this).transition().attr('r', 7).attr('fill', '#ff4500');
                        })
                        .on('mouseout', function () {
                            setTooltipData(null);
                            d3.select(this).transition().attr('r', 5).attr('fill', colors[i]);
                        });
            }

        });

    }, [data, containerWidth, height, customLabelFormatter, yAxisLabelType, currency, colors, customLegend]);

    return (
        <div ref={wrapperRef} style={{ width: '100%', height: '100%' }}>
            <div style={{ position: 'relative' }}>
                <svg ref={svgRef} width="100%" height={height} />
                {
                    customLegend &&
                    customLegend

                }
            </div>
            {tooltipData &&
                <Box sx={{
                    position: 'absolute',
                    left: tooltipData.x,
                    top: tooltipData.y,
                    pointerEvents: 'none',
                    backgroundColor: '#2EB273',
                    zIndex: '99999',
                    border: 'none',
                    borderRadius: '3px',
                    transform: 'translate(-50%, -100%)',
                    padding: '2px 6px 2px 6px',
                    gap: '10px'
                }}>
                    <span style={{ fontSize: '16px', weight: '500', color: '#ffff' }}>
                        {tooltipData.content}
                    </span>
                </Box>
            }
        </div>
    );
};

export { AreaLineChart };
export default AreaLineChart;
